/**
 * <StudentLiveQuizPage> — le quiz de fin de séance, hors du live (page).
 *
 * L'élève sort du live (le tuteur l'a clôturé, ou il est parti avant, après sa note) : Sam lui
 * propose 8 questions sur ce qu'il vient de travailler, ses erreurs d'abord. Il est
 * toujours facultatif : le second bouton mène à la suite et le dit (« Voir mon compte rendu »,
 * « Retour à l'accueil ») — pas « Plus tard », le quiz ne se retrouve nulle part ensuite.
 *
 * Le gabarit du quiz de prérequis (`<StudentPrerequisiteQuizPage>`) : Sam sur scène à l'offre, qui
 * **se range** dans l'en-tête à la première question (`<ChampionStage>`), comme « Étudier avec
 * Sam ». Le jeu, lui, est celui du « Défi éclair » (`<CorrectionChallenge>`) : juste ou faux tout
 * de suite, la bonne réponse en vert, la série dès deux bonnes d'affilée. Trois étapes (`step`,
 * contrôlée) :
 *  - `loading` : Sam sur scène, qui prépare le quiz (une attente en mots, `<WaitingText>`) ;
 *  - `offer` : Sam pose sa question, l'effort d'abord (« 2 minutes pour ne rien oublier de ta
 *    séance ? »), puis combien de questions, dont combien de ratées (« 8 questions, dont 1 que tu
 *    avais ratée » : la séance est dans le titre, ses chapitres ne se nomment pas), et le pétale à
 *    gagner ; « Je commence » et la suite ;
 *  - `question` : à côté de Sam rangé, « 3 / 8 », l'avancée et la série ; la question (une formule
 *    se rend comme l'énoncé, `textFormat="rich"`), ses réponses ; répondue, le mot qui dit si
 *    c'était juste, puis « Question suivante » (« Voir mon score » à la dernière). La croix fait
 *    partir : les réponses données sont gardées, sans pétale ;
 *  - `result` : Sam dit le score à côté de lui ; le pétale gagné, ce qui reviendra (seulement si
 *    chaque réponse est arrivée : une réponse refusée n'est pas comptée ratée), et la
 *    correction de toutes les questions (`<QuizReviewQuestion>`, repliées : juste ou faux se lit
 *    d'un coup d'œil, chacune se déplie), puis « Continuer ».
 * Pas de « Rejouer » : un quiz par sortie du live.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ProgressBar } from "../../atoms/ProgressBar"
import { StepStage } from "../../atoms/StepStage"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { Card } from "../../molecules/Card"
import { CardList, CardListRow } from "../../molecules/CardList"
import { ChampionStage } from "../../molecules/ChampionStage"
import { ConversationClose, ConversationHeader } from "../../molecules/ConversationHeader"
import { CorrectionMoment } from "../../molecules/CorrectionMoment"
import { GainChip } from "../../molecules/GainChip"
import { QuizChoiceRow } from "../../molecules/QuizChoiceRow"
import { quizChoiceLetter } from "../../molecules/QuizChoiceRow/QuizChoiceRow"
import { QuizReviewQuestion } from "../../molecules/QuizReviewQuestion"
import { RichText } from "../../molecules/RichText"
import { CHALLENGE_STREAK_FROM, challengeChoiceState } from "../../organisms/CorrectionChallenge/challengeGame"
import { PageTemplate } from "../../templates/PageTemplate"
import { type UIQuizReviewQuestion } from "../../../ports/primary/ui/types/studentQuizReview"
import { LIVE_QUIZ_TEXTS } from "./liveQuizTexts"

/** Une question du quiz : ses réponses arrivent déjà mélangées, la bonne repérée par son rang. */
export type UILiveQuizQuestion = {
    id: number
    text: string
    /** `rich` : la question porte une formule. Défaut `plain`. */
    textFormat?: "plain" | "rich"
    /** Telles que l'élève les voit, formules comprises. */
    options: string[]
    /** Le format de chaque réponse, au même rang : `rich` pour une réponse à formule. Défaut `plain`. */
    optionFormats?: ("plain" | "rich")[]
    correctIndex: number
}

export type StudentLiveQuizStep =
    | { kind: "loading" }
    | {
          kind: "offer"
          /** Le nombre de questions, de 3 à 8. */
          total: number
          minutes: number
          /** Les questions ratées pendant la séance qu'il reprend. 0 : la ligne ne se dit pas. */
          missed: number
          /** Le pétale à gagner. 0 : pas de gain annoncé (déjà gagné sur ce live). */
          petals: number
      }
    | {
          kind: "question"
          /** À partir de 0. */
          index: number
          total: number
          question: UILiveQuizQuestion
          /** La réponse touchée ; `null` tant que l'élève n'a pas répondu. */
          selected: number | null
          /** Les bonnes réponses d'affilée, réponse en cours comprise. */
          streak: number
      }
    | {
          kind: "result"
          right: number
          total: number
          /** Le pétale est gagné : toutes les questions sont répondues, une fois par live. */
          earned: boolean
          /** Chaque réponse est arrivée au serveur : les ratées reviendront. */
          saved: boolean
          /** Toutes les questions, relues, dans l'ordre du quiz. */
          review: UIQuizReviewQuestion[]
      }

/** Où mène la suite : le rapport (le tuteur a clôturé), ou l'accueil (l'élève est parti avant). */
export type StudentLiveQuizExit = "report" | "home"

export type StudentLiveQuizPageProps = {
    step: StudentLiveQuizStep
    /** Où mène la suite : son nom est celui du second bouton de l'offre. */
    exitTo: StudentLiveQuizExit
    onStart: () => void
    /** Le second bouton de l'offre : la suite, sans quiz. */
    onLater: () => void
    onAnswer: (index: number) => void
    /** « Question suivante », « Voir mon score ». */
    onNext: () => void
    /** La croix, pendant les questions : partir, les réponses gardées. */
    onLeave: () => void
    /** « Continuer », au résultat : le rapport du live, ou l'accueil. */
    onContinue: () => void
    testID?: string
}

/** Une question se lit dans une colonne de téléphone, même sur un grand écran. */
const QUIZ_WIDTH = 560

export function StudentLiveQuizPage(props: StudentLiveQuizPageProps) {
    const { step, onLeave, testID } = props

    return (
        <PageTemplate
            header={
                <ConversationHeader
                    name={LIVE_QUIZ_TEXTS.title}
                    maxContentWidth={QUIZ_WIDTH}
                    end={
                        step.kind === "question" ? (
                            <ConversationClose onPress={onLeave} accessibilityLabel={LIVE_QUIZ_TEXTS.leave} />
                        ) : undefined
                    }
                />
            }
            scroll={false}
            maxContentWidth={QUIZ_WIDTH}
            padding="4"
            footer={<Footer {...props} />}
            testID={testID}
        >
            <View style={styles.fill}>
                <ChampionStage
                    name="sam"
                    docked={step.kind !== "offer" && step.kind !== "loading"}
                    stage={<Offer step={step} />}
                    dock={<Dock step={step} />}
                >
                    {/* Une question à la fois : la précédente sort, la suivante entre. */}
                    <StepStage stepKey={step.kind === "question" ? `question-${step.index}` : step.kind} fill>
                        <ScrollView
                            style={styles.fill}
                            contentContainerStyle={styles.scroll}
                            showsVerticalScrollIndicator={false}
                        >
                            <Step {...props} />
                        </ScrollView>
                    </StepStage>
                </ChampionStage>
            </View>
        </PageTemplate>
    )
}

/** Sur scène, sous Sam : ce qu'il propose, et ce que ça rapporte. */
function Offer({ step }: { step: StudentLiveQuizStep }) {
    if (step.kind === "loading") return <WaitingText color="text-muted">{LIVE_QUIZ_TEXTS.preparing}</WaitingText>
    if (step.kind !== "offer") return null
    return (
        <CorrectionMoment title={LIVE_QUIZ_TEXTS.offer(step.minutes)} champion={false}>
            <Stack gap="2" align="center">
                <Text size="text-md" color="text-muted" center>
                    {LIVE_QUIZ_TEXTS.size(step.total)}
                    {step.missed > 0 ? `, ${LIVE_QUIZ_TEXTS.missed(step.missed)}` : ""}
                </Text>
                {step.petals > 0 && (
                    <Row gap="2" align="center" justify="center" style={styles.reward}>
                        <GainChip amount={step.petals} unit="petals" />
                        <Text size="text-sm" color="text-muted">
                            {LIVE_QUIZ_TEXTS.reward}
                        </Text>
                    </Row>
                )}
            </Stack>
        </CorrectionMoment>
    )
}

/** À côté de Sam rangé : où en est le quiz, ou ce qu'il dit du score. */
function Dock({ step }: { step: StudentLiveQuizStep }) {
    if (step.kind === "question") {
        return (
            <Stack gap="2">
                <Row justify="between" align="center">
                    <Text size="text-sm" weight="semibold" color="text-muted">
                        {LIVE_QUIZ_TEXTS.count(step.index, step.total)}
                    </Text>
                    {step.streak >= CHALLENGE_STREAK_FROM && (
                        <Badge variant="warning" iconName="fire" text={LIVE_QUIZ_TEXTS.streak(step.streak)} />
                    )}
                </Row>
                <ProgressBar value={step.total > 0 ? (step.index / step.total) * 100 : 0} height={6} />
            </Stack>
        )
    }
    if (step.kind === "result") {
        return (
            <Stack gap="1">
                <Text size="text-2xl" weight="bold" color="text-strong">
                    {LIVE_QUIZ_TEXTS.score(step.right, step.total)}
                </Text>
                <Text size="text-md" color="text-muted">
                    {LIVE_QUIZ_TEXTS.verdict(step.right, step.total)}
                </Text>
            </Stack>
        )
    }
    return null
}

/** Sous Sam rangé : la question et ses réponses, ou le pétale et la correction. */
function Step(props: StudentLiveQuizPageProps) {
    const { step, onAnswer } = props

    if (step.kind === "result") {
        const missed = step.total - step.right
        return (
            <Stack gap="5">
                <Stack gap="2" align="center">
                    {step.earned && (
                        <Row justify="center">
                            <GainChip amount={1} unit="petals" />
                        </Row>
                    )}
                    {missed > 0 && step.saved && (
                        <Text size="text-md" color="text-muted" center>
                            {LIVE_QUIZ_TEXTS.missedBack(missed)}
                        </Text>
                    )}
                </Stack>
                {step.review.length > 0 && (
                    <Stack gap="2">
                        <SectionLabel>{LIVE_QUIZ_TEXTS.correction}</SectionLabel>
                        <CardList>
                            {step.review.map((question, index) => (
                                <CardListRow key={question.id} py="0">
                                    <QuizReviewQuestion index={index} question={question} />
                                </CardListRow>
                            ))}
                        </CardList>
                    </Stack>
                )}
            </Stack>
        )
    }
    if (step.kind !== "question") return null

    const { question, selected } = step
    const answered = selected != null
    return (
        <Stack gap="4">
            <Card p="4">
                {question.textFormat === "rich" ? (
                    <RichText value={question.text} />
                ) : (
                    <Text size="text-lg" weight="semibold" color="text-strong">
                        {question.text}
                    </Text>
                )}
            </Card>
            <Stack gap="2">
                {question.options.map((option, index) => (
                    <QuizChoiceRow
                        key={index}
                        letter={quizChoiceLetter(index)}
                        label={option}
                        labelFormat={question.optionFormats?.[index]}
                        state={challengeChoiceState(index, question.correctIndex, selected)}
                        onPress={answered ? undefined : () => onAnswer(index)}
                    />
                ))}
            </Stack>
            {answered && (
                <Text
                    size="text-md"
                    weight="bold"
                    color={selected === question.correctIndex ? "success-strong" : "text-muted"}
                    center
                >
                    {selected === question.correctIndex ? LIVE_QUIZ_TEXTS.right : LIVE_QUIZ_TEXTS.wrong}
                </Text>
            )}
        </Stack>
    )
}

function Footer(props: StudentLiveQuizPageProps) {
    const { step, exitTo, onStart, onLater, onNext, onContinue } = props
    if (step.kind === "loading") return null
    if (step.kind === "offer") {
        return (
            <Stack gap="2">
                <Button label={LIVE_QUIZ_TEXTS.start} iconName="play" onPress={onStart} fullWidth />
                <Button label={LIVE_QUIZ_TEXTS.exit[exitTo]} variant="ghost" onPress={onLater} fullWidth />
            </Stack>
        )
    }
    if (step.kind === "result") {
        return <Button label={LIVE_QUIZ_TEXTS.continue} onPress={onContinue} fullWidth />
    }
    const last = step.index >= step.total - 1
    return (
        <Button
            label={last ? LIVE_QUIZ_TEXTS.seeScore : LIVE_QUIZ_TEXTS.next}
            onPress={onNext}
            disabled={step.selected == null}
            fullWidth
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, minHeight: 0 },
    scroll: { paddingBottom: theme.spacing["4"] },
    reward: { marginTop: theme.spacing["2"] },
}))
